/**
 * DebugLogger — URL-activated, feature-scoped debug logging.
 *
 * A portable, zero-overhead logging utility that activates via a URL parameter.
 * No code changes needed to toggle logging in dev, test, or production.
 *
 * FuseSimple uses this internally for its own subsystems (see `src/core/FuseSimple.ts`)
 * and re-exports it publicly so consumer apps can register their own tags and benefit
 * from the same `?debug=` flag and the `isProduction` lockdown.
 *
 * ## Quick Start (consumer app)
 *
 * ```ts
 * import { createDebugLogger } from "@mapsimple/fusesimple";
 *
 * const debugLogger = createDebugLogger({
 *   name: "MYAPP",
 *   features: ["FETCH", "RENDER", "AUTH"],
 *   isProduction: import.meta.env.PROD, // hard lockdown in prod builds
 * });
 *
 * debugLogger.log("FETCH", { url: "...", status: 200 });
 * debugLogger.log("BUG", { bugId: "BUG-001", category: "RENDER", description: "..." });
 * ```
 *
 * ## URL Activation
 *
 * - `?debug=all`           — Enable all registered tags for every logger instance.
 * - `?debug=FETCH,SYNC`    — Enable specific tags (comma-separated, case-insensitive).
 *                            Each logger instance filters against its own `features` list,
 *                            so tags unknown to that instance are silently ignored.
 * - `?debug=false`         — Explicitly disable all logging.
 * - (no param)             — Logging disabled (zero overhead).
 *
 * ## Production Lockdown
 *
 * Pass `isProduction: true` to the constructor (or via `FuseSimple.create({ isProduction: true })`
 * for FuseSimple's internal logger) to hard-disable everything:
 *
 * - `?debug=` URL param is ignored entirely.
 * - `log()` no-ops for every tag.
 * - **BUG level is also silenced** (unlike the dev default, where BUG always logs).
 *
 * Use this in production builds where end-users could otherwise hit `?debug=all` and
 * inspect internals.
 *
 * ## Features
 *
 * - **URL-driven:** No code changes to toggle. Works in dev, test, prod (unless locked down).
 * - **Feature-scoped:** Each tag isolates a subsystem. No firehose.
 * - **BUG level:** Always logs via `console.warn` by default; silenced only by `isProduction`.
 * - **Iframe-aware:** Checks `window.parent` for `?debug=` param (useful when a consumer
 *   embeds FuseSimple inside an ExB widget or other iframed host).
 * - **Zero overhead:** When no `?debug=` param is present, `isEnabled()` returns false
 *   immediately. Lazy init — URL params not parsed until the first `log()` call.
 * - **Multi-instance safe:** Each `DebugLogger` instance holds its own `features` list
 *   and `enabledFeatures` set. Consumer and FuseSimple loggers coexist on the same URL
 *   param without interfering — each instance only reacts to tags it knows about, and
 *   each tags its output with its own `name` prefix.
 */

/**
 * Feature tag type. Any uppercase string is valid — callers register their own tags
 * via `DebugLoggerOptions.features`.
 */
export type DebugFeature = string;

export interface DebugLoggerOptions {
  /** Display name used as the log prefix, e.g. `"FUSESIMPLE"` or `"MYAPP"`. */
  name: string;
  /**
   * Registered feature tags for this instance. A tag in `?debug=` is honored only
   * if it appears in this list (case-insensitive match). `?debug=all` enables every
   * entry here.
   */
  features: string[];
  /**
   * Hard lockdown for production builds. When `true`:
   *
   * - URL params are ignored entirely.
   * - `isEnabled()` always returns `false`, **including for `BUG`**.
   * - `log()` no-ops unconditionally, **including for `BUG`**.
   *
   * Default: `false`.
   */
  isProduction?: boolean;
}

export class DebugLogger {
  private enabledFeatures: Set<DebugFeature> = new Set();
  private initialized = false;
  private name: string;
  private features: DebugFeature[];
  private isProduction: boolean;

  constructor(options: DebugLoggerOptions) {
    this.name = options.name;
    this.features = options.features;
    this.isProduction = options.isProduction ?? false;
  }

  /**
   * Parse `?debug=` from the URL on first use.
   * Checks both the current window and the parent window (for iframe hosts).
   * Short-circuits entirely when `isProduction` is `true`.
   */
  private initialize(): void {
    if (this.initialized) return;

    // Production lockdown: skip URL parsing entirely. Nothing will ever be enabled.
    if (this.isProduction) {
      this.initialized = true;
      return;
    }

    let urlParams = new URLSearchParams(window.location.search);
    let debugValue = urlParams.get("debug");

    // If not found in the current window, check the parent. Needed when a consumer
    // embeds FuseSimple inside an iframed host (e.g. Experience Builder).
    if (debugValue === null && window.parent !== window) {
      try {
        urlParams = new URLSearchParams(window.parent.location.search);
        debugValue = urlParams.get("debug");
      } catch (e) {
        // Cross-origin restriction — parent location is not accessible. Normal.
      }
    }

    if (debugValue === "false") {
      // Explicitly disabled.
      this.initialized = true;
      return;
    }

    if (debugValue === "all") {
      // Enable every registered feature for this instance.
      for (const feature of this.features) {
        if (feature !== "all" && feature !== "false") {
          this.enabledFeatures.add(feature);
        }
      }
      console.log(`[${this.name}-DEBUG] Enabled ALL features:`, Array.from(this.enabledFeatures));
    } else if (debugValue !== null) {
      // Parse comma-separated feature list (case-insensitive).
      const requestedFeatures = debugValue
        .split(",")
        .map((f) => f.trim().toUpperCase() as DebugFeature);
      for (const feature of requestedFeatures) {
        if (feature === "ALL") {
          for (const f of this.features) {
            if (f !== "all" && f !== "false") {
              this.enabledFeatures.add(f);
            }
          }
        } else if (this.features.includes(feature)) {
          this.enabledFeatures.add(feature);
        }
        // Tags not in this instance's `features` list are silently ignored — that's
        // how multiple loggers can share the same URL param without colliding.
      }
    }

    this.initialized = true;
  }

  /**
   * Check whether a feature tag is currently enabled.
   * `BUG` level is always enabled in dev — unless `isProduction` is `true`, which
   * overrides everything including `BUG`.
   */
  private isEnabled(feature: DebugFeature): boolean {
    // Production lockdown: nothing is enabled, including BUG.
    if (this.isProduction) return false;

    this.initialize();

    // BUG level always enabled in dev.
    if (feature === "BUG") return true;

    return this.enabledFeatures.has(feature);
  }

  /**
   * Log structured data if the feature tag is enabled.
   *
   * @param feature - Tag name (must be registered in `features`, or `"BUG"`).
   * @param data - Any data to log (serialized as JSON).
   *
   * `BUG` level: Always logs via `console.warn` in dev, even with `?debug=false`.
   * Use it for known issues that should surface until a real fix lands. Include
   * `bugId`, `category`, and `description` fields for consistency.
   *
   * When `isProduction` is `true`, this method no-ops for every tag including `BUG`.
   */
  log(feature: DebugFeature, data: Record<string, unknown>): void {
    // Production lockdown: no logging of any kind, ever.
    if (this.isProduction) return;

    // BUG level always logs (in dev).
    if (feature === "BUG") {
      const logData = {
        feature: "BUG",
        bugId: data?.bugId ?? "UNKNOWN",
        category: data?.category ?? "GENERAL",
        timestamp: new Date().toISOString(),
        ...data,
      };

      console.warn(
        `[${this.name.toUpperCase()} \u26A0\uFE0F BUG]`,
        JSON.stringify(logData, null, 2),
      );
      return;
    }

    // Regular feature logging — only fires when the tag is enabled via ?debug=.
    if (!this.isEnabled(feature)) return;

    const logData = {
      feature,
      timestamp: new Date().toISOString(),
      ...data,
    };

    console.log(`[${this.name.toUpperCase()}-${feature}]`, JSON.stringify(logData, null, 2));
  }

  /**
   * Returns the current debug configuration. Useful for diagnostic displays
   * and for runtime checks of lockdown state.
   */
  getConfig(): {
    enabledFeatures: string[];
    debugValue: string | null;
    isProduction: boolean;
  } {
    if (this.isProduction) {
      return { enabledFeatures: [], debugValue: null, isProduction: true };
    }

    this.initialize();

    // Same iframe logic as initialize().
    let debugValue = new URLSearchParams(window.location.search).get("debug");
    if (debugValue === null && window.parent !== window) {
      try {
        debugValue = new URLSearchParams(window.parent.location.search).get("debug");
      } catch (e) {
        // Cross-origin — parent not accessible.
      }
    }

    return {
      enabledFeatures: Array.from(this.enabledFeatures),
      debugValue,
      isProduction: false,
    };
  }
}

/**
 * Factory helper. Equivalent to `new DebugLogger(options)` but reads nicely at
 * consumer call sites and matches the MapSimple family's `create*` convention.
 *
 * @example
 * ```ts
 * const debugLogger = createDebugLogger({
 *   name: "MYAPP",
 *   features: ["FETCH", "RENDER", "AUTH"],
 *   isProduction: import.meta.env.PROD,
 * });
 * ```
 */
export function createDebugLogger(options: DebugLoggerOptions): DebugLogger {
  return new DebugLogger(options);
}
